/** Median — each stroke is pulled toward the shape of the one before it. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { buildSmoothPath, distance } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8c4 0 4 8 8 8s4-8 8-8" opacity="0.45"/><path d="M4 16c4 0 4-8 8-8s4 8 8 8" opacity="0.45"/><path d="M4 12h16"/></svg>';

export function createMedianShapes(): CreateModule {
  let pts: Point[] = [];
  let control: Point[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'median',
      kind: 'create',
      name: 'Median',
      apiVersion: 1,
      description: 'Each stroke is pulled toward the shape of the stroke before it.',
      icon: ICON,
      params: [{ kind: 'slider', key: 'blend', label: 'Blend', min: 0, max: 1, step: 0.05, default: 0.5 }],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 1.5) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const blend = cfgNum(ctx.config, 'blend', 0.5);
      let shaped: Point[];
      if (control.length < 2) {
        shaped = pts;
      } else {
        shaped = pts.map((p, i) => {
          const ci = Math.round((i / Math.max(1, pts.length - 1)) * (control.length - 1));
          const cp = control[ci];
          return { x: p.x + (cp.x - p.x) * blend, y: p.y + (cp.y - p.y) * blend };
        });
      }
      const style = defaultStyle();
      style.color = { ...ctx.ink };
      style.lineWidth = 2;
      return [ctx.newShape({ path: buildSmoothPath(shaped), style })];
    },

    onCommit() {
      if (pts.length >= 2) control = pts.map((p) => ({ ...p }));
      pts = [];
    },

    onClear() {
      pts = [];
    },

    onReselect() {
      control = [];
    },
  };
}
